/**
 * SegmentForm — F12.I E2.T4a + E2.T5.
 *
 * Dispatcher per typ segmentu numeracji. Renderuje inline form dla wybranego
 * NumberingSegment.type. 9 form per typ:
 *   text             — input "Wartość" (PREFIX)
 *   separator        — predefined buttony (/ - . space) + custom input
 *   year             — toggle YYYY / YY
 *   month/day/quarter — info read-only (brak parametrów)
 *   counter          — input padding 1-10
 *   field_value      — field picker + transform dropdown + firstN
 *   conditional_text — field picker + mapping editor (lista par + _default)
 */

import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import type { NumberingSegment } from '@/lib/numbering-preview';

interface ModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
}

interface Props {
  segment: NumberingSegment;
  onChange: (next: NumberingSegment) => void;
  /** Pola modułu — używane przez field_value / conditional_text form. */
  fields?: ModuleField[];
  /** Slug do data-testid (np. slug kategorii + index segmentu). */
  testIdPrefix: string;
}

// ─── Predefined separator options ────────────────────────────────────────────

const SEPARATOR_OPTIONS = ['/', '-', '.', ' '] as const;

// ─── Field picker (shared dla field_value + conditional_text) ────────────────

interface FieldPickerProps {
  value: string;
  onChange: (slug: string) => void;
  fields: ModuleField[];
  testId: string;
}

function FieldPicker({ value, onChange, fields, testId }: FieldPickerProps) {
  const { t } = useTranslation();
  if (fields.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border-primary px-3 py-2 text-xs text-text-muted">
        {t('tenant.numbering.segments.fieldPicker.empty')}
      </p>
    );
  }
  // F12.I E2.T7 (D13): walidator inline — gdy fieldSlug jest ustawione ale nie
  // istnieje w fields modułu (np. pole usunięte po skonfigurowaniu numeracji),
  // pokaż warning. UI ostrzega adminowi że segment będzie pusty przy generowaniu numeru.
  const isMissing = value !== '' && !fields.some((f) => f.slug === value);
  return (
    <div className="space-y-1.5">
      <Dropdown
        value={value}
        onChange={(v) => onChange(v)}
        className={`block w-full rounded-md border bg-bg-primary px-3 py-2 text-sm text-text-primary ${
          isMissing ? 'border-error-500' : 'border-border-primary'
        }`}
        data-testid={testId}
        options={[
          { value: '', label: t('tenant.numbering.segments.fieldPicker.placeholder') },
          ...(isMissing
            ? [
                {
                  value,
                  label: `${value} (${t('tenant.numbering.segments.fieldPicker.missingOptionLabel')})`,
                },
              ]
            : []),
          ...fields.map((f) => ({ value: f.slug, label: `${f.name} (${f.slug})` })),
        ]}
      />
      {isMissing && (
        <p className="text-xs text-error-500" data-testid={`${testId}-missing-warning`}>
          {t('tenant.numbering.segments.fieldPicker.missingWarning', { fieldSlug: value })}
        </p>
      )}
    </div>
  );
}

// ─── Dispatcher ──────────────────────────────────────────────────────────────

export function SegmentForm({ segment, onChange, fields, testIdPrefix }: Props) {
  const { t } = useTranslation();
  const moduleFields = fields ?? [];

  switch (segment.type) {
    // ─── text ────────────────────────────────────────────────────────────
    case 'text':
      return (
        <FormField label={t('tenant.numbering.segments.text.value')}>
          <Input
            value={segment.value}
            onChange={(e) => onChange({ ...segment, value: e.target.value })}
            placeholder="PREFIX"
            data-testid={`${testIdPrefix}-text-value`}
          />
        </FormField>
      );

    // ─── separator ───────────────────────────────────────────────────────
    case 'separator': {
      const isPreset = SEPARATOR_OPTIONS.includes(
        segment.value as (typeof SEPARATOR_OPTIONS)[number],
      );
      return (
        <FormField label={t('tenant.numbering.segments.separator.value')}>
          <div className="flex flex-wrap items-center gap-2">
            {SEPARATOR_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ ...segment, value: opt })}
                className={`rounded-md border px-3 py-1.5 text-sm font-mono ${
                  segment.value === opt
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-border-primary bg-bg-secondary text-text-secondary hover:bg-bg-tertiary'
                }`}
                data-testid={`${testIdPrefix}-separator-preset-${opt === ' ' ? 'space' : opt}`}
              >
                {opt === ' ' ? `␣ ${t('tenant.numbering.segments.separator.options.space')}` : opt}
              </button>
            ))}
            <Input
              value={isPreset ? '' : segment.value}
              onChange={(e) => onChange({ ...segment, value: e.target.value })}
              placeholder={t('tenant.numbering.segments.separator.options.custom')}
              maxLength={4}
              className="w-32"
              data-testid={`${testIdPrefix}-separator-custom`}
            />
          </div>
        </FormField>
      );
    }

    // ─── year ────────────────────────────────────────────────────────────
    case 'year':
      return (
        <FormField label={t('tenant.numbering.segments.year.format')}>
          <div className="flex flex-wrap gap-2">
            {(['YYYY', 'YY'] as const).map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => onChange({ ...segment, format: fmt })}
                className={`rounded-md border px-3 py-1.5 text-sm font-mono ${
                  (segment.format ?? 'YYYY') === fmt
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-border-primary bg-bg-secondary text-text-secondary hover:bg-bg-tertiary'
                }`}
                data-testid={`${testIdPrefix}-year-${fmt}`}
              >
                {t(`tenant.numbering.segments.year.${fmt}`)}
              </button>
            ))}
          </div>
        </FormField>
      );

    // ─── month / day / quarter (no params, info only) ────────────────────
    case 'month':
      return <p className="text-xs text-text-muted">{t('tenant.numbering.segments.month.info')}</p>;
    case 'day':
      return <p className="text-xs text-text-muted">{t('tenant.numbering.segments.day.info')}</p>;
    case 'quarter':
      return (
        <p className="text-xs text-text-muted">{t('tenant.numbering.segments.quarter.info')}</p>
      );

    // ─── counter ─────────────────────────────────────────────────────────
    case 'counter': {
      const padding = segment.padding ?? 4;
      const sampleNumber = String(1).padStart(padding, '0');
      return (
        <FormField
          label={t('tenant.numbering.segments.counter.padding')}
          hint={t('tenant.numbering.segments.counter.paddingHint', { sample: sampleNumber })}
        >
          <Input
            type="number"
            min={1}
            max={10}
            value={padding}
            onChange={(e) =>
              onChange({
                ...segment,
                padding: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 4)),
              })
            }
            className="w-24"
            data-testid={`${testIdPrefix}-counter-padding`}
          />
        </FormField>
      );
    }

    // ─── field_value ─────────────────────────────────────────────────────
    case 'field_value': {
      const showFirstN = segment.transform === 'first_n';
      return (
        <div className="space-y-3">
          <FormField label={t('tenant.numbering.segments.field_value.fieldSlug')}>
            <FieldPicker
              value={segment.fieldSlug}
              onChange={(slug) => onChange({ ...segment, fieldSlug: slug })}
              fields={moduleFields}
              testId={`${testIdPrefix}-field_value-fieldSlug`}
            />
          </FormField>
          <FormField label={t('tenant.numbering.segments.field_value.transform')}>
            <Dropdown
              value={segment.transform ?? ''}
              onChange={(next) => {
                onChange({
                  ...segment,
                  transform:
                    next === '' ? undefined : (next as 'uppercase' | 'lowercase' | 'first_n'),
                  firstN: next === 'first_n' ? (segment.firstN ?? 3) : undefined,
                });
              }}
              className="block w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary"
              data-testid={`${testIdPrefix}-field_value-transform`}
              options={[
                { value: '', label: t('tenant.numbering.segments.field_value.transformNone') },
                {
                  value: 'uppercase',
                  label: t('tenant.numbering.segments.field_value.transformUppercase'),
                },
                {
                  value: 'lowercase',
                  label: t('tenant.numbering.segments.field_value.transformLowercase'),
                },
                {
                  value: 'first_n',
                  label: t('tenant.numbering.segments.field_value.transformFirstN'),
                },
              ]}
            />
          </FormField>
          {showFirstN && (
            <FormField label={t('tenant.numbering.segments.field_value.firstN')}>
              <Input
                type="number"
                min={1}
                max={50}
                value={segment.firstN ?? 3}
                onChange={(e) =>
                  onChange({
                    ...segment,
                    firstN: Math.min(50, Math.max(1, parseInt(e.target.value, 10) || 3)),
                  })
                }
                className="w-24"
                data-testid={`${testIdPrefix}-field_value-firstN`}
              />
            </FormField>
          )}
        </div>
      );
    }

    // ─── conditional_text ────────────────────────────────────────────────
    case 'conditional_text': {
      const entries = Object.entries(segment.mapping ?? {});
      const nonDefault = entries.filter(([k]) => k !== '_default');
      const defaultText = segment.mapping?.['_default'] ?? '';

      const updateMapping = (next: Record<string, string>) => {
        onChange({ ...segment, mapping: next });
      };

      const updateEntry = (oldKey: string, newKey: string, newValue: string) => {
        const next = { ...segment.mapping };
        if (oldKey !== newKey) delete next[oldKey];
        next[newKey] = newValue;
        updateMapping(next);
      };

      const deleteEntry = (key: string) => {
        const next = { ...segment.mapping };
        delete next[key];
        updateMapping(next);
      };

      const addEntry = () => {
        let idx = 1;
        while (segment.mapping?.[`value${idx}`] !== undefined) idx++;
        updateMapping({ ...segment.mapping, [`value${idx}`]: '' });
      };

      return (
        <div className="space-y-3">
          <FormField label={t('tenant.numbering.segments.conditional_text.fieldSlug')}>
            <FieldPicker
              value={segment.fieldSlug}
              onChange={(slug) => onChange({ ...segment, fieldSlug: slug })}
              fields={moduleFields}
              testId={`${testIdPrefix}-conditional_text-fieldSlug`}
            />
          </FormField>

          <FormField
            label={t('tenant.numbering.segments.conditional_text.mappingTitle')}
            hint={t('tenant.numbering.segments.conditional_text.mappingHint')}
          >
            <div className="space-y-1.5">
              {nonDefault.map(([key, value], idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={key}
                    onChange={(e) => updateEntry(key, e.target.value, value)}
                    placeholder={t('tenant.numbering.segments.conditional_text.keyPlaceholder')}
                    className="flex-1"
                    data-testid={`${testIdPrefix}-conditional_text-key-${idx}`}
                  />
                  <span className="text-text-muted" aria-hidden="true">
                    →
                  </span>
                  <Input
                    value={value}
                    onChange={(e) => updateEntry(key, key, e.target.value)}
                    placeholder={t('tenant.numbering.segments.conditional_text.valuePlaceholder')}
                    className="flex-1"
                    data-testid={`${testIdPrefix}-conditional_text-value-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => deleteEntry(key)}
                    aria-label={t('tenant.numbering.segments.conditional_text.deleteEntry')}
                    className="shrink-0 text-text-muted hover:text-error-500"
                    data-testid={`${testIdPrefix}-conditional_text-delete-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addEntry}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-primary bg-bg-secondary px-3 py-1 text-xs text-text-secondary hover:bg-bg-tertiary"
                data-testid={`${testIdPrefix}-conditional_text-add`}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('tenant.numbering.segments.conditional_text.addEntry')}
              </button>
            </div>
          </FormField>

          <FormField
            label={t('tenant.numbering.segments.conditional_text.defaultLabel')}
            hint={t('tenant.numbering.segments.conditional_text.defaultHint')}
          >
            <Input
              value={defaultText}
              onChange={(e) => updateMapping({ ...segment.mapping, _default: e.target.value })}
              placeholder={t('tenant.numbering.segments.conditional_text.defaultPlaceholder')}
              data-testid={`${testIdPrefix}-conditional_text-default`}
            />
          </FormField>
        </div>
      );
    }
  }
}
